<template>
  <SoDialog
    v-model="value"
    :card-title="group ? `Edit ${group.name}` : ''"
    :max-width="640"
    :loading="isSaving"
    :persistent="isSaving"
    data-testid="banner-edit-dialog"
  >
    <template v-if="group">
      <p
        v-for="note in notes"
        :key="note"
        class="text-body-2 text-medium-emphasis mb-2"
        data-testid="banner-edit-note"
      >
        {{ note }}
      </p>
      <SoForm @submit="save">
        <SoTabs
          v-if="group.rows.length > 1"
          v-model="activeTab"
          divider
          data-testid="banner-edit-tabs"
        >
          <SoTab
            v-for="row in group.rows"
            :key="row.id ?? row.variantKey"
            :title="tabLabel(row)"
            eager
          >
            <MarketingBannerCopyForm
              v-if="forms[rowKey(row)]"
              :model-value="forms[rowKey(row)]"
              :errors="errorsFor(row)"
              :note="tabNote(row)"
              class="pt-4"
              @update:model-value="onInput(row, $event)"
            />
          </SoTab>
        </SoTabs>
        <MarketingBannerCopyForm
          v-else-if="forms[rowKey(group.primary)]"
          :model-value="forms[rowKey(group.primary)]"
          :errors="errorsFor(group.primary)"
          @update:model-value="onInput(group.primary, $event)"
        />
        <SoCheckbox
          v-model="confirmed"
          label="I understand customers see these changes within about a minute"
          class="mt-6"
          data-testid="banner-edit-confirm-checkbox"
        />
        <div class="d-flex justify-end mt-6">
          <SoButton
            title="Cancel"
            class="mr-2"
            large
            :disabled="isSaving"
            data-testid="banner-edit-cancel"
            @click="value = false"
          />
          <SoButton
            :title="saveTitle"
            type="success"
            large
            :loading="isSaving"
            :disabled="!canSave"
            data-testid="banner-edit-save"
            @click="save"
          />
        </div>
      </SoForm>
    </template>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import {
    IBannerGroup,
    IMarketingBanner,
  } from "@/models/alertsBanners/alertsBanners.types"
  import { VARIANT_TAB_LABELS } from "@/models/alertsBanners/marketingBannerConfig"
  import {
    BannerCopyErrors,
    BannerCopyField,
    changedCopy,
    copyErrors,
    copyOf,
    IBannerCopy,
    serverFieldErrors,
  } from "@/views/admin/marketingBanners/marketingBannerCopy"
  import {
    bannerNotes,
    VARIANT_NOTES,
  } from "@/views/admin/marketingBanners/marketingBannerNotes"
  import { AlertsBannersMutator } from "@/mutators/alertsBanners/AlertsBannersMutator"
  import { useSnackbarStore } from "@/stores/snackbar"
  import MarketingBannerCopyForm from "../components/MarketingBannerCopyForm.vue"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoCheckbox,
      SoForm,
      SoTabs,
      SoTab,
      MarketingBannerCopyForm,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      group: {
        type: Object as PropType<IBannerGroup | null>,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        activeTab: 0,
        forms: {} as Record<string, IBannerCopy>,
        saved: {} as Record<string, IBannerCopy>,
        serverErrors: {} as Record<string, BannerCopyErrors>,
        confirmed: false,
        isSaving: false,
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      notes(): string[] {
        return this.group ? bannerNotes(this.group) : []
      },
      activeRow(): IMarketingBanner | null {
        return this.group?.rows[this.activeTab] ?? null
      },
      activeChanges(): Partial<IBannerCopy> {
        return this.activeRow ? this.changesFor(this.activeRow) : {}
      },
      canSave(): boolean {
        if (!this.activeRow) return false
        const key = this.rowKey(this.activeRow)
        return (
          !this.isSaving &&
          this.confirmed &&
          Object.keys(this.activeChanges).length > 0 &&
          Object.keys(copyErrors(this.forms[key])).length === 0 &&
          Object.keys(this.serverErrors[key] ?? {}).length === 0
        )
      },
      saveTitle(): string {
        if (!this.group || !this.activeRow || this.group.rows.length < 2)
          return "Save"
        return `Save ${this.tabLabel(this.activeRow).toLowerCase()} copy`
      },
    },
    watch: {
      modelValue: {
        handler(open: boolean) {
          if (open) this.populate()
        },
        immediate: true,
      },
    },
    methods: {
      rowKey(row: IMarketingBanner): string {
        return String(row.id ?? row.variantKey)
      },
      tabLabel(row: IMarketingBanner): string {
        return row.variant ? VARIANT_TAB_LABELS[row.variant] : row.name
      },
      tabNote(row: IMarketingBanner): string | undefined {
        return row.variant ? VARIANT_NOTES[row.variant] : undefined
      },
      populate() {
        const rows = this.group?.rows ?? []
        this.activeTab = 0
        this.confirmed = false
        this.serverErrors = {}
        this.saved = Object.fromEntries(
          rows.map((row) => [this.rowKey(row), copyOf(row)]),
        )
        this.forms = Object.fromEntries(
          rows.map((row) => [this.rowKey(row), copyOf(row)]),
        )
      },
      changesFor(row: IMarketingBanner): Partial<IBannerCopy> {
        const key = this.rowKey(row)
        return this.forms[key] && this.saved[key]
          ? changedCopy(this.saved[key], this.forms[key])
          : {}
      },
      errorsFor(row: IMarketingBanner): BannerCopyErrors {
        const key = this.rowKey(row)
        return {
          ...this.serverErrors[key],
          ...(this.forms[key] ? copyErrors(this.forms[key]) : {}),
        }
      },
      onInput(row: IMarketingBanner, next: IBannerCopy) {
        const key = this.rowKey(row)
        const previous = this.forms[key]
        const errors = this.serverErrors[key] ?? {}
        this.serverErrors = {
          ...this.serverErrors,
          [key]: Object.fromEntries(
            Object.entries(errors).filter(
              ([field]) =>
                previous?.[field as BannerCopyField] ===
                next[field as BannerCopyField],
            ),
          ),
        }
        this.forms = { ...this.forms, [key]: next }
      },
      async save() {
        const row = this.activeRow
        if (!this.canSave || !this.group || !row || row.id === null) return
        const key = this.rowKey(row)
        const changes = this.activeChanges
        const snackbar = useSnackbarStore()
        this.isSaving = true
        try {
          const { failed } = await AlertsBannersMutator.updateRow(
            row.id,
            changes,
          )
          if (failed.length) {
            this.serverErrors = {
              ...this.serverErrors,
              [key]: serverFieldErrors(failed[0].fields),
            }
            snackbar.addError(
              `Could not save ${this.group.name}: ${failed[0].message}`,
            )
            return
          }
          this.saved = { ...this.saved, [key]: { ...this.forms[key] } }
          this.confirmed = false
          snackbar.addSuccess(
            `${this.group.name} saved. Customers see it within about a minute.`,
          )
          const othersUnsaved = this.group.rows.some(
            (other) => Object.keys(this.changesFor(other)).length > 0,
          )
          if (!othersUnsaved) this.value = false
        } finally {
          this.isSaving = false
        }
      },
    },
  })
</script>
